import React, {useEffect} from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate, useLocation  } from "react-router-dom";
import logo from "../assets/transparent-logo.png";
import "./layout.css";
import { UserDataType } from "../validation/business.validation";

interface SidebarProps {
  openToggle: (open: boolean) => void;
  menuItems: { label: string; icon: React.ReactNode; path: string }[];
  userData?: UserDataType | null;
  mobileOpen?: boolean;
  mobileToggle?: (open: boolean) => void;
}

const CommonSidebar: React.FC<SidebarProps> = ({
  openToggle,
  menuItems,
  userData,
  mobileOpen,
  mobileToggle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [lockedOpen, setLockedOpen] = React.useState(true);
  const showText = lockedOpen;

  useEffect(() => {
    if (!isMobile) return;
    if (typeof mobileOpen === "boolean") {
      setLockedOpen(mobileOpen);
    } else {
      setLockedOpen(false);
    }
  }, [isMobile, mobileOpen]);

  const handleToggle = () => {
    // Desktop toggle only (sidebar collapse/expand)
    setLockedOpen((prev) => {
      const next = !prev;
      openToggle(next);
      return next;
    });
  };

  const renderMenuItem = ({ label, icon, path }: typeof menuItems[0]) => {
    const selected = location.pathname === path;
    return (
      <ListItemButton
        key={path}
        onClick={() => navigate(path)}
        selected={selected}
        sx={{
          justifyContent: lockedOpen ? "flex-start" : "center",
          borderRadius: 2,
          mx: 1,
          mb: 0.5,
          minHeight: 48,
          "&.Mui-selected, &.Mui-selected:hover": {
            bgcolor: "#004D95",
            color: "#fff",
          },
          "&:hover": { bgcolor: "#23263A" },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: lockedOpen ? 2 : "auto",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          {icon}
        </ListItemIcon>
        {lockedOpen && <ListItemText primary={label} />}
      </ListItemButton>
    );
  };

  return (
    <Box
      className="sidebar-root"
      sx={{
        width: lockedOpen ? 250 : 72,
        transition: "width 0.2s",
        position: "relative",
        bgcolor: "#181C32",
        color: "#fff",
        minHeight: "100vh",
        height: "100vh",
        borderRight: "1px solid #23263A",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          justifyContent: lockedOpen ? "space-between" : "center",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {showText && (
          <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            <img src={userData?.logo || logo} alt="Logo" className="sidebar-logo" />
            <Typography
              className="sidebar-title"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 150,
                ml: 1,
              }}
            >
              {`${(userData as any)?.firstName || ''} ${(userData as any)?.middleName || ''} ${(userData as any)?.lastName || ''}`.trim() || userData?.name || ""}
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <Tooltip title={lockedOpen ? "Collapse" : "Lock Open"}>
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={{ ml: showText ? 1 : 0, color: "#fff" }}
            >
              {lockedOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
        )}

        {isMobile && (
          <Tooltip title={lockedOpen ? "Close" : "Open"}>
            <IconButton
              size="small"
              onClick={() => mobileToggle?.(!mobileOpen)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "#fff",
              }}
              aria-label={lockedOpen ? "Close sidebar" : "Open sidebar"}
            >
              {lockedOpen ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Menu Items */}
      <List
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          py: 1,
          pr: "2px",
          scrollbarGutter: "stable",
          scrollbarWidth: "thin",
          scrollbarColor: "#9CA3AF #252A3F",
          "&::-webkit-scrollbar": {
            width: 10,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#9CA3AF",
            borderRadius: 8,
            border: "2px solid #252A3F",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#252A3F",
            borderRadius: 8,
          },
        }}
      >
        {menuItems.map(renderMenuItem)}
      </List>

      {/* Footer */}
      {lockedOpen && (
        <Typography
          variant="body2"
          align="center"
          sx={{ color: "#b0b3c6", my: 2, flexShrink: 0 }}
        >
          &copy; {new Date().getFullYear()} The Business Landing Hub
        </Typography>
      )}
    </Box>
  );
};

export default CommonSidebar;
